import React, { useMemo, useState } from "react";
import { AddCircle, Tornado } from "@mui/icons-material";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import Button from "@mui/material/Button";
import {
  FormControl,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Box,
} from "@mui/material";
import { DUHS_RFG } from "../Constants/PolicyTranslationConstants";
import PolicyRulesDataGrid from "../DataGrids/PolicyRulesDataGrid";
import ProcedureCodeDialog from "../../PolicyTranslation/Dialogs/ProcedureCodeDialog";
import ActionsFragment from "../Fragments/ActionsFragment";
import UseCasesActionsFragment from "../Fragments/UseCasesActionsFragment";
import RadioGenderFragment from "../Fragments/RadioGenderFragment";
import StartDateFragment from "../Fragments/StartDateFragment";
import DuplicateRowFragment from "../Fragments/DuplicateRowFragment";
import { useLocation } from "react-router-dom";
import styled from "@emotion/styled";
import GenderAgeMeetCC from "../Fragments/GenderAgeMeetCC";
import { getLobName, getAgeRange } from "../Utils/TranslationUtils";
import RadioYearHandlingFragment from "../Fragments/RadioYearHandlingFragment";
import { renderCellClass, renderError } from "../Validation/ValidationHelper";
import AllowedTimeFrameFragment from "../Fragments/AllowedTimeFrameFragment";
import { showVarianceIcon } from "utils/shared";
import StyledTornadoIcon from "components/TornadoIcon";

const AddProcedureCode = styled("a")(({ theme }) => {
  return {
    textTransform: "uppercase" /* Make text uppercase */,
    color: "blue" /* Standard hyperlink color */,
    pointerEvents: "none" /* Make the link not clickable */,
    cursor: "default" /* Change cursor to default */,
  };
});

const compactInputSx = {
  height: 32,
  fontSize: 13,
  "& .MuiInputBase-root": {
    height: 32,
    fontSize: 13,
  },
  "& .MuiSelect-select, & input": {
    padding: "4px 8px",
    height: "100%",
    display: "flex",
    alignItems: "center",
  },
};

const dxRulesOptions = [
  "Can't Have - Both",
  "Can't Have - DOS",
  "Can't Have - Frequency",
  "Must Have - Both",
  "Must Have - DOS",
  "Must Have - Frequency",
]
  .sort()
  .reduce((acc, val) => [...acc, val], ["None"]);

const RenderDxRulesDropdown = (selectedValue, editable, onChange) => {
  const valueToUse = dxRulesOptions.includes(selectedValue)
    ? selectedValue
    : "None";
  return (
    <FormControl fullWidth size="small">
      <Select
        value={valueToUse}
        onChange={(e) => onChange(e.target.value)}
        disabled={!editable}
        displayEmpty
        size="small"
        sx={compactInputSx}
      >
        {dxRulesOptions.map((option) => (
          <MenuItem key={option} value={option} sx={{ fontSize: 13 }}>
            {option}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

const HistoryTranslationTable = ({
  gridHeight,
  setLoading,
  updateRulesAfterEdit,
  availableProcedureCodes,
  chips,
  editable,
  newRowStructure,
  rows,
  setSelectedCC,
  addNewRows,
  duplicateRow,
  selectedCC,
  handleDelete,
  policyData,
  pageComponent,
  handleRemoveInvalidRow,
  endExistingRule,
}) => {
  const [isPxDialogOpen, setIsPxDialogOpen] = useState(false);
  const { state } = useLocation();

  const selectedHealthPlan = state?.policyState?.healthPlanName;
  const handleAddProcedureCode = () => {
    setIsPxDialogOpen(true);
  };

  const handleAddRowToGrid = (checkedItems) => {
    addNewRows(checkedItems, DUHS_RFG);
  };

  const columns = [
    {
      display: "flex",
      field: "CoverageCriteriaList",
      headerName: "CC#",
      flex: 0.3,
      editable: editable,
      valueGetter: (value, row) => {
        return row.RuleFacts.CoverageCriteriaList || "";
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.CoverageCriteriaList = value ? value?.trim() : "";
        return newRow;
      },
    },
    {
      display: "flex",
      field: "procedureCode",
      headerName: "Procedure Code",
      flex: 0.6,
      renderCell: (params) => (
        <Box
          display="flex"
          flexDirection="row"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <DuplicateRowFragment
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            value={params.row.RuleFacts.ProcedureCode}
            duplicateRow={duplicateRow}
          />
          {/* This is used to identify variances in new rules and rules that
              have completed the sheet generation process */}
          {editable &&
            showVarianceIcon(params.row.RuleHeader, selectedHealthPlan) && (
              <StyledTornadoIcon />
            )}
        </Box>
      ),
      valueGetter: (value, row) => row.RuleFacts.ProcedureCode,
    },
    {
      display: "flex",
      field: "dxRules",
      headerName: "DX Rules",
      flex: 1,
      renderCell: (params) =>
        RenderDxRulesDropdown(
          params.row.RuleFacts.DXLimitation ?? "",
          editable,
          (newValue) => {
            const updatedRow = structuredClone(params.row);
            updatedRow.RuleFacts.DXLimitation = newValue; // Set DXLimitation
            updateRulesAfterEdit(updatedRow, params.row);
          }
        ),
      valueGetter: (value, row) => row.RuleFacts?.DXLimitation ?? "None",
      valueSetter: (value, row) => {
        const newRow = structuredClone(row);
        newRow.RuleFacts.DXLimitation = value;
        return newRow;
      },
    },
    {
      display: "flex",
      field: "approvedUnitCount",
      headerName: "Approved Unit Count",
      editable: editable,
      flex: 0.5,
      valueGetter: (value, row) => {
        return row.RuleFacts.ApprovedUnitCount;
      },
      valueSetter: (value, row) => {
        let newRow = structuredClone(row);
        newRow.RuleFacts.ApprovedUnitCount = value?.trim() || "";
        return newRow;
      },
    },
    {
      display: "flex",
      field: "allowedTimeFrame",
      headerName: "Allowed Time Frame",
      flex: 1,
      renderCell: (params) => (
        <AllowedTimeFrameFragment
          updateRulesAfterEdit={updateRulesAfterEdit}
          editable={editable && pageComponent === "PolicyTranslation"}
          params={params}
          compactInputSx={compactInputSx}
        />
      ),
    },
    {
      display: "flex",
      field: "displayHistoryYear",
      headerName: "Year Handling",
      flex: 0.7,
      renderCell: (params) => (
        <RadioYearHandlingFragment
          editable={
            editable &&
            pageComponent === "PolicyTranslation" &&
            params.row.RuleHeader.EditRule !== "EDIT"
          }
          params={params}
          updateRulesAfterEdit={updateRulesAfterEdit}
        />
      ),
    },
    {
      display: "flex",
      field: "Modifiers",
      headerName: "Modifiers",
      flex: 1,
      renderCell: (params) => {
        const modReq = params.row.RuleFacts?.ModifierRequired ?? "";
        const modExc = params.row.RuleFacts?.ModifierExcluded ?? "";

        const onBlurModReq = (e) => {
          const newValue = e.target.value;
          if (newValue !== modReq) {
            const updatedRow = structuredClone(params.row);
            updatedRow.RuleFacts.ModifierRequired = newValue;
            updateRulesAfterEdit(updatedRow, params.row);
          }
        };

        const onBlurModExc = (e) => {
          const newValue = e.target.value;
          if (newValue !== modExc) {
            const updatedRow = structuredClone(params.row);
            updatedRow.RuleFacts.ModifierExcluded = newValue;
            updateRulesAfterEdit(updatedRow, params.row);
          }
        };

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              sx={compactInputSx}
              label="Req"
              defaultValue={modReq}
              disabled={!editable}
              onBlur={onBlurModReq}
            />
            <span> - </span>
            <TextField
              variant="outlined"
              size="small"
              sx={compactInputSx}
              label="Excl"
              defaultValue={modExc}
              disabled={!editable}
              onBlur={onBlurModExc}
            />
          </div>
        );
      },
    },
    {
      display: "flex",
      field: "genderAgeMeetsCC",
      headerName: "Gender & Age Meets CC?",
      flex: 1,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderEditCell: (params) => (
        <GenderAgeMeetCC
          editable={
            editable &&
            pageComponent === "PolicyTranslation" &&
            params.row.RuleHeader.EditRule !== "EDIT"
          }
          params={params}
          updateRulesAfterEdit={updateRulesAfterEdit}
          direction="vertical"
        />
      ),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return (
          <GenderAgeMeetCC
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
            direction="vertical"
          />
        );
      },
    },
    {
      display: "flex",
      editable: editable,
      field: "coveredGender",
      headerName: "Gender",
      flex: 0.7,

      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return (
          <RadioGenderFragment
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
            direction="vertical"
          />
        );
      },
      renderEditCell: (params) => {
        return (
          <RadioGenderFragment
            editable={
              editable &&
              pageComponent === "PolicyTranslation" &&
              params.row.RuleHeader.EditRule !== "EDIT"
            }
            params={params}
            updateRulesAfterEdit={updateRulesAfterEdit}
            direction="vertical"
          />
        );
      },
    },
    {
      field: "coveredAgeRange",
      headerName: "Age Range",
      flex: 1,
      sortable: false,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderEditCell: (params) => {
        const ageLow = params.row.RuleFacts?.ageLow ?? "";
        const ageHigh = params.row.RuleFacts?.ageHigh ?? "";
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              sx={compactInputSx}
              label="Low"
              defaultValue={ageLow}
              disabled={!editable}
              onChange={(e) => {
                const newValue = e.target.value;
                const updatedRow = structuredClone(params.row);
                updatedRow.RuleFacts.ageLow = newValue;
                updatedRow.RuleFacts.DisplayAgeRange = `${newValue},${ageHigh}`;
                updateRulesAfterEdit(updatedRow, params.row);
              }}
            />
            <span> - </span>
            <TextField
              variant="outlined"
              size="small"
              sx={compactInputSx}
              label="High"
              defaultValue={ageHigh}
              disabled={!editable}
              onChange={(e) => {
                const newValue = e.target.value;
                const updatedRow = structuredClone(params.row);
                updatedRow.RuleFacts.ageHigh = newValue;
                updatedRow.RuleFacts.DisplayAgeRange = `${ageLow},${newValue}`;
                updateRulesAfterEdit(updatedRow, params.row);
              }}
            />
          </div>
        );
      },
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }

        const ageLow = params.row.RuleFacts?.ageLow ?? "";
        const ageHigh = params.row.RuleFacts?.ageHigh ?? "";

        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <TextField
              variant="outlined"
              size="small"
              sx={compactInputSx}
              label="Low"
              defaultValue={ageLow}
              disabled={!editable}
              onChange={(e) => {
                const newValue = e.target.value;
                const updatedRow = structuredClone(params.row);
                updatedRow.RuleFacts.ageLow = newValue;
                updatedRow.RuleFacts.DisplayAgeRange = `${newValue},${ageHigh}`;
                updateRulesAfterEdit(updatedRow, params.row);
              }}
            />
            <span> - </span>
            <TextField
              variant="outlined"
              size="small"
              sx={compactInputSx}
              label="High"
              defaultValue={ageHigh}
              disabled={!editable}
              onChange={(e) => {
                const newValue = e.target.value;
                const updatedRow = structuredClone(params.row);
                updatedRow.RuleFacts.ageHigh = newValue;
                updatedRow.RuleFacts.DisplayAgeRange = `${ageLow},${newValue}`;
                updateRulesAfterEdit(updatedRow, params.row);
              }}
            />
          </div>
        );
      },
    },
    {
      display: "flex",
      field: "startDate",
      headerName: "Date & LOB Configuration",
      flex: 1.2,
      editable: editable,
      cellClassName: (params) => renderCellClass(params),
      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);

        if (error) {
          return error;
        }

        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
            endExistingRule={endExistingRule}
          />
        );
      },

      renderEditCell: (params) => {
        return (
          <StartDateFragment
            updateRulesAfterEdit={updateRulesAfterEdit}
            handleRemoveInvalidRow={handleRemoveInvalidRow}
            editable={editable && pageComponent === "PolicyTranslation"}
            params={params}
            policyData={policyData}
          />
        );
      },
    },
    {
      display: "flex",
      field: "LobDisplayName",
      headerName: "LOB",
      minWidth: 150,
      valueGetter: (value, row) => {
        return getLobName(row.RuleFacts.LobConfigKey, policyData);
      },
      cellClassName: (params) => renderCellClass(params),

      renderCell: (params) => {
        const { colDef, row } = params;
        const error = renderError(colDef.field, row);
        if (error) {
          return error;
        }
        return params.value || "";
      },
    },
    {
      display: "flex",
      field: "actions",
      headerName: "Actions",
      flex: 1,
      renderCell: (params) => {
        return pageComponent === "PolicyTranslation" ? (
          <ActionsFragment
            handleDelete={handleDelete}
            editable={editable}
            params={params}
          />
        ) : (
          <UseCasesActionsFragment
            editable={editable && pageComponent === "UseCases"}
            params={params}
          />
        );
      },
    },
  ];

  const sanitizedRows = useMemo(() => {
    return rows.map((row) => {
      const sanitizedRow = { ...row };
      const ageRange = getAgeRange(
        row?.RuleFacts?.DisplayAgeRange,
        row?.RuleFacts?.InvalidAgeRange,
        row?.RuleFacts?.DisplayAgeGenderRule
      );
      sanitizedRow.RuleFacts = { ...row.RuleFacts };

      sanitizedRow.RuleFacts.ageHigh = ageRange[1];
      sanitizedRow.RuleFacts.ageLow = ageRange[0];
      return sanitizedRow;
    });
  }, [rows]);

  return (
    <div style={{ width: "100%" }}>
      {pageComponent !== "UseCases" && (
        <Box>
          <Button
            onClick={handleAddProcedureCode}
            variant="text"
            startIcon={<AddCircleIcon color="primary" />}
          >
            Add Procedure Code
          </Button>
        </Box>
      )}
      <PolicyRulesDataGrid
        gridHeight={gridHeight}
        setLoading={setLoading}
        editable={editable && pageComponent === "PolicyTranslation"}
        rows={sanitizedRows}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: "procedureCode", sort: "asc" }],
          },
        }}
        processRowUpdate={(newRow, oldRow) =>
          updateRulesAfterEdit(newRow, oldRow)
        }
        rowHeight={50}
      />
      <ProcedureCodeDialog
        availableProcedureCodes={availableProcedureCodes}
        selectedCC={selectedCC}
        setSelectedCC={setSelectedCC}
        chips={chips}
        open={isPxDialogOpen}
        onClose={() => setIsPxDialogOpen(false)}
        onSave={handleAddRowToGrid}
      />
    </div>
  );
};

export default HistoryTranslationTable;
